/**
 * Done. / ChiroCPD — Certificate Page (/cert/:moduleId)
 *
 * Standalone certificate view. Fetches GET /api/modules/:moduleId/cert and
 * renders the CertCard with a Download / Print PDF button.
 * No video, no quiz — just the certificate.
 */

import { useEffect, useState } from 'react';
import { Link, useRoute } from 'wouter';
import { AppLayout } from '@/layouts/AppLayout';
import { CertCard } from '@/components/features/CertCard';
import { api, ApiError } from '@/lib/api';
import type { ModuleCertificate } from '@/lib/types';

function CertificateContent({ moduleId }: { moduleId: string }): React.ReactElement {
  const [cert, setCert] = useState<ModuleCertificate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.get<ModuleCertificate>(`/api/modules/${moduleId}/cert`);
        if (!cancelled) setCert(data);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) return;
        if (err instanceof ApiError && err.status === 404) {
          setError('No passing certificate found for this module.');
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load certificate.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [moduleId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <span className="material-symbols-outlined animate-spin text-[var(--stitch-primary)] text-4xl">progress_activity</span>
      </div>
    );
  }

  if (error || !cert) {
    return (
      <div className="max-w-xl mx-auto py-16 text-center flex flex-col items-center gap-4">
        <span className="material-symbols-outlined text-5xl text-[var(--stitch-outline)]">workspace_premium</span>
        <p className="stitch-font-body text-[var(--stitch-on-surface-variant)]">
          {error ?? 'Certificate not found.'}
        </p>
        <Link
          href="/dashboard"
          className="stitch-font-label text-[13px] text-[var(--stitch-primary)] hover:underline uppercase tracking-wider"
        >
          ← Back to dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto">
      <div className="mb-6">
        <Link
          href="/dashboard"
          className="stitch-font-label text-[13px] text-[var(--stitch-primary)] no-underline inline-flex items-center gap-1"
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span> Dashboard
        </Link>
      </div>

      <h1 className="stitch-font-display text-[28px] text-[var(--stitch-primary)] uppercase tracking-tight leading-tight mb-8">
        Your Certificate
      </h1>

      <CertCard cert={cert} />
    </div>
  );
}

export function CertificatePage(): React.ReactElement {
  const [, params] = useRoute<{ moduleId: string }>('/cert/:moduleId');
  const moduleId = params?.moduleId ?? '';
  return (
    <AppLayout currentRoute="/dashboard">
      {moduleId ? <CertificateContent moduleId={moduleId} /> : <p>Certificate not found.</p>}
    </AppLayout>
  );
}
